'use client';

import styles from './project-detail.module.scss';
import { useRouter } from "next/navigation";
import { useContentSize } from "@/components/SizeProvider";
import { formatDesktopWindowTitle, getTitleFontSize } from '@/lib/utils/typography';
import VideoPlayer from "@/components/VideoPlayer";
import DraggableWindow from "@/components/DraggableWindow";
import { useEffect, useMemo, useRef, useState } from "react";
import { ProjectModel } from "@/lib/models/Project";
import ProjectInfo from './ProjectInfo';
import { useZIndexManager } from '@/lib/hooks/useZIndexManager';
import { RouterName } from '@/lib/enum/RouterEnum';
import { PROJECT_THUMBNAIL_ASPECT_RATIO } from '@/lib/constants/media';

interface Props {
  project: ProjectModel;
  projects: ProjectModel[];
  presetIndex: number;
}

interface Position {
  x: number;
  y: number;
}

interface PresetSet {
  projectInfoPosition: Position;
  featuredVideoPosition: Position;
  featuredImagePosition: Position;
  draggableWindowPositions: Position[];
}

// Module-level so it survives ProjectDetail unmount/remount on navigation.
// Stores the absolute timestamp (ms) at which the next scroll-driven
// navigation is allowed to fire.
let scrollNavCooldownUntil = 0;
const WHEEL_NAVIGATION_COOLDOWN_MS = 2000;

const FALLBACK_POSITION: Position = { x: 0, y: 0 };

export default function ProjectDetail({ project, projects, presetIndex }: Props) {
  const _router = useRouter();
  const { width: widthContent, height: heightContent } = useContentSize();
  const { getZIndex, handleElementClick } = useZIndexManager({ wrapAround: 50 });

  const titleFontSize = getTitleFontSize(widthContent);
  const draggableWindowWidth = Math.floor(widthContent * 0.234);
  const draggableWindowSize = {
    width: draggableWindowWidth,
    height: Math.ceil(draggableWindowWidth * PROJECT_THUMBNAIL_ASPECT_RATIO) + titleFontSize + 24,
  };
  const featuredImageWidth = widthContent * 0.5;
  const featuredImageSize = {
    width: featuredImageWidth,
    height: featuredImageWidth * PROJECT_THUMBNAIL_ASPECT_RATIO + titleFontSize + 24,
  };
  const videoPlayerWidth = widthContent * 0.5;
  const videoPlayerSize = {
    width: videoPlayerWidth,
    height: videoPlayerWidth * PROJECT_THUMBNAIL_ASPECT_RATIO + titleFontSize + 24,
  };
  const projectInfoSize = {
    width: Math.floor(widthContent * 0.28),
    height: 200,
  };
  const { prevProjectHref, nextProjectHref } = useMemo(() => {
    const currentIndex = projects.findIndex(item => item.id === project.id);
    if (currentIndex === -1) return { prevProjectHref: '', nextProjectHref: '' };
    const prevIndex = currentIndex === 0 ? projects.length - 1 : currentIndex - 1;
    const nextIndex = currentIndex === projects.length - 1 ? 0 : currentIndex + 1;
    return {
      prevProjectHref: `${RouterName.projectDetail}/${projects[prevIndex].slug}`,
      nextProjectHref: `${RouterName.projectDetail}/${projects[nextIndex].slug}`,
    };
  }, [project, projects]);

  const hrefsRef = useRef({ prevProjectHref, nextProjectHref });
  useEffect(() => {
    hrefsRef.current = { prevProjectHref, nextProjectHref };
  }, [prevProjectHref, nextProjectHref]);

  const [isCollapsing, setIsCollapsing] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);

  const presets = useMemo<PresetSet[] | null>(() => {
    if (widthContent <= 0 || heightContent <= 0) return null;
    const videoPlayerControlsHeight = 42; // Got this value from the css
    return [
      {
        projectInfoPosition: { x: window.innerWidth * 0.04, y: window.innerHeight * 0.05 },
        featuredVideoPosition: { x: window.innerWidth * 0.5 - videoPlayerSize.width / 2, y: window.innerHeight * 0.50 - videoPlayerSize.height / 2 - videoPlayerControlsHeight },
        featuredImagePosition: { x: window.innerWidth * 0.5 - featuredImageSize.width / 2, y: window.innerHeight * 0.5 - featuredImageSize.height / 2 },
        // This NEEDS to be an exhaustive list, and accessors should NEVER access out of bounds.
        draggableWindowPositions: [
          { x: window.innerWidth * 0.66, y: window.innerHeight * 0.07 },
          { x: window.innerWidth * 0.02, y: window.innerHeight * 0.5 },
          { x: window.innerWidth * 0.20, y: window.innerHeight * 0.7 },
          { x: window.innerWidth * 0.74, y: window.innerHeight * 0.62 },
        ],
      },
      {
        projectInfoPosition: { x: window.innerWidth * 0.06, y: window.innerHeight * 0.02 },
        featuredVideoPosition: { x: window.innerWidth * 0.5 - videoPlayerSize.width / 2, y: window.innerHeight * 0.50 - videoPlayerSize.height / 2 - videoPlayerControlsHeight },
        featuredImagePosition: { x: window.innerWidth * 0.5 - featuredImageSize.width / 2, y: window.innerHeight * 0.5 - featuredImageSize.height / 2 },
        // This NEEDS to be an exhaustive list, and accessors should NEVER access out of bounds.
        draggableWindowPositions: [
          { x: window.innerWidth * 0.70, y: window.innerHeight * 0.05 },
          { x: window.innerWidth * 0.764, y: window.innerHeight * 0.42 },
          { x: window.innerWidth * 0.71, y: window.innerHeight * 0.70 },
          { x: window.innerWidth * 0.03, y: window.innerHeight * 0.60 },
        ],
      },
    ];
  }, [widthContent, heightContent, videoPlayerSize.width, videoPlayerSize.height, featuredImageSize.width, featuredImageSize.height]);

  useEffect(() => {
    window.addEventListener('wheel', onWheel);
    return () => {
      window.removeEventListener('wheel', onWheel);
    }
  }, [])

  const onWheel = (event: WheelEvent) => {
    const now = Date.now();
    if (now < scrollNavCooldownUntil) {
      return;
    }
    scrollNavCooldownUntil = now + WHEEL_NAVIGATION_COOLDOWN_MS;

    const targetHref = event.deltaY < 0 ? hrefsRef.current.prevProjectHref : hrefsRef.current.nextProjectHref;
    collapseAndNavigate(targetHref);
  }

  // Handle navigation after collapse animation completes
  useEffect(() => {
    if (isCollapsing && pendingNavigationRef.current) {
      const timer = setTimeout(() => {
        _router.push(pendingNavigationRef.current!);
        pendingNavigationRef.current = null;
      }, 300); // Make sure this value matches the collapse animation duration for DraggableWindow, VideoPlayer and ProjectInfo.

      return () => clearTimeout(timer);
    }
  }, [isCollapsing, _router]);

  const collapseAndNavigate = (href: string) => {
    // console.log('triggering collapse animation, navigating to:', href);
    setIsCollapsing(true);
    pendingNavigationRef.current = href;
  };

  // Assumes draggable's pivot point is at top left
  const constrainDraggableToViewport = (pos: { x: number; y: number }, size: { width: number; height: number }) => {
    const constrainedX = Math.max(-size.width / 2, Math.min(window.innerWidth - size.width / 2, pos.x));
    const constrainedY = Math.max(-size.height / 2, Math.min(window.innerHeight - size.height / 2, pos.y));
    return { x: constrainedX, y: constrainedY };
  };

  return (
    project &&
    <div
      className={styles['darken']}
      onClick={() => collapseAndNavigate(RouterName.dashboard)}
    >
      {project.projectMedia
        .filter((_, index) => index >= 1)
        .map((mediaSrc: string, index: number) => {
          const id = `project-${index}`;
          return (
            <DraggableWindow
              key={`${project.projectTitle}-${index}`}
              title={formatDesktopWindowTitle(project.projectTitle, index + 2)}
              src={mediaSrc}
              initialPosition={presets?.[presetIndex]?.draggableWindowPositions[index] ?? FALLBACK_POSITION}
              size={draggableWindowSize}
              mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
              allowDragging
              constrainingFunction={constrainDraggableToViewport}
              animationDelay={0.2 + index * 0.1}
              autoplay
              zIndex={getZIndex(id)}
              onPointerDownCapture={() => handleElementClick(id)}
              isOnLandingPage={false}
              widthContent={widthContent}
              isCollapsing={isCollapsing}
            />
          )
        })
      }

      <ProjectInfo
        key={`${project.projectTitle}-info`}
        title={formatDesktopWindowTitle(project.projectTitle, project.projectMedia.length + 1)}
        client={project.clientName}
        services={project.services}
        description={project.projectDescription}
        initialPosition={presets?.[presetIndex]?.projectInfoPosition ?? FALLBACK_POSITION}
        size={projectInfoSize}
        constrainingFunction={constrainDraggableToViewport}
        animationDelay={0.1}
        zIndex={getZIndex('projectInfo')}
        onPointerDownCapture={() => handleElementClick('projectInfo')}
        widthContent={widthContent}
        isCollapsing={isCollapsing}
      />

      {project.projectMedia[0].endsWith('.mp4') ? (
        <VideoPlayer
          key={`${project.projectTitle}-video`}
          title={formatDesktopWindowTitle(project.projectTitle, 1)}
          src={project.projectMedia[0]}
          initialPosition={presets?.[presetIndex]?.featuredVideoPosition ?? FALLBACK_POSITION}
          size={videoPlayerSize}
          mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
          allowDragging
          constrainingFunction={constrainDraggableToViewport}
          zIndex={getZIndex('featuredMedia')}
          onPointerDownCapture={() => handleElementClick('featuredMedia')}
          isOnLandingPage={false}
          prevProjectHref={prevProjectHref}
          nextProjectHref={nextProjectHref}
          widthContent={widthContent}
          isCollapsing={isCollapsing}
          onNavigate={collapseAndNavigate}
        />
      ) : (
        <DraggableWindow
          key={`${project.projectTitle}-featured`}
          title={formatDesktopWindowTitle(project.projectTitle, 1)}
          src={project.projectMedia[0]}
          initialPosition={presets?.[presetIndex]?.featuredImagePosition ?? FALLBACK_POSITION}
          size={featuredImageSize}
          mediaAspectRatio={PROJECT_THUMBNAIL_ASPECT_RATIO}
          allowDragging
          constrainingFunction={constrainDraggableToViewport}
          zIndex={getZIndex('featuredMedia')}
          onPointerDownCapture={() => handleElementClick('featuredMedia')}
          isOnLandingPage={false}
          prevProjectHref={prevProjectHref}
          nextProjectHref={nextProjectHref}
          widthContent={widthContent}
          isCollapsing={isCollapsing}
          onNavigate={collapseAndNavigate}
          eager
        />
      )}
    </div>
  )
}
